import axios from 'axios';

import * as local from './local';
import * as types from './types';

export { local, types };

export const $axios = axios.create();

export function getAuthHeaders(access_token: string) {
  return {
    Authorization: `Bearer ${access_token}`,
  };
}

export function getLoginUrl() {
  return `/api/auth/redirect`;
}

export function createUserToken(code: string) {
  return $axios.get<types.UserToken>('/api/auth/complete', {
    params: {
      code,
    },
    skipAuthRefresh: true,
  } as any);
}

export function refreshUserToken(refresh_token: string) {
  return $axios.post<types.UserToken>('/api/auth/refresh', refresh_token, {
    skipAuthRefresh: true,
  } as any);
}

export function getUser(access_token: string) {
  return $axios.get<types.User>('/api/user', {
    headers: getAuthHeaders(access_token),
  });
}

export function getUsersOrganizations(
  access_token: string,
  user_id: types.User['id']
) {
  return $axios.get<types.OrganizationsResponse>(
    `/users/${user_id}/organizations/`,
    {
      headers: getAuthHeaders(access_token),
      baseURL: `${process.env.REACT_APP_TALENT_API_URL}`,
      params: {
        limit: 100,
      },
    }
  );
}

export function getOrganization(
  access_token: string,
  orgId: types.Organization['id']
) {
  return $axios.get<types.Organization>(`/organizations/${orgId}/`, {
    headers: getAuthHeaders(access_token),
    baseURL: `${process.env.REACT_APP_TALENT_API_URL}`,
    params: {
      include_fields: 'has_actual_agreement,has_approved_legal_entity',
    },
  });
}

export function getEvents(
  access_token: string,
  params: types.EventRequestParams
) {
  return $axios.get<types.EventsResponse>(`/events/`, {
    headers: getAuthHeaders(access_token),
    baseURL: `${process.env.REACT_APP_TALENT_API_URL}`,
    params,
  });
}

export function getEvent(access_token: string, id: types.Event['id']) {
  return $axios.get<types.Event>(`/api/events/${id}`, {
    headers: getAuthHeaders(access_token),
  });
}

export function getEventFields(access_token: string, id: types.Event['id']) {
  return $axios.get<types.EventFields[]>(`/api/events/${id}/fields`, {
    headers: getAuthHeaders(access_token),
  });
}

export function getEventTemplates(access_token: string, id: types.Event['id']) {
  return $axios.get<types.Template[]>(`/api/events/${id}/templates`, {
    headers: getAuthHeaders(access_token),
  });
}

export function createEventTemplate(
  access_token: string,
  eventId: types.Event['id'],
  input: types.TemplateInput
) {
  return $axios.post<types.Template>(
    `/api/events/${eventId}/templates`,
    input,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

export function deleteTemplate(
  access_token: string,
  templateId: types.Template['id']
) {
  return $axios.delete<void>(`/api/templates/${templateId}`, {
    headers: getAuthHeaders(access_token),
  });
}

export function getTemplate(
  access_token: string,
  templateId: types.Template['id']
) {
  return $axios.get<types.Template>(`/api/templates/${templateId}`, {
    headers: getAuthHeaders(access_token),
  });
}

export function updateTemplate(
  access_token: string,
  templateId: types.Template['id'],
  input: types.TemplateInput
) {
  return $axios.put<types.Template>(`/api/templates/${templateId}`, input, {
    headers: getAuthHeaders(access_token),
  });
}

export function getTemplateBlocks(
  access_token: string,
  templateId: types.Template['id']
) {
  return $axios.get<local.TemplateBlock[]>(
    `/api/templates/${templateId}/blocks`,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

export function deleteTemplateBlock(
  access_token: string,
  id: local.TemplateBlock['id']
) {
  return $axios.delete(`/api/templates/blocks/${id}`, {
    headers: getAuthHeaders(access_token),
  });
}

export function updateTemplateBlock(
  access_token: string,
  block: local.TemplateBlock
) {
  return $axios.put<local.TemplateBlock>(
    `/api/templates/blocks/${block.id}`,
    block,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

export function createTemplateBlock(
  access_token: string,
  input: local.TemplateBlockInput,
  templateId: types.Template['id']
) {
  return $axios.post<local.TemplateBlock>(
    `/api/templates/${templateId}/blocks`,
    input,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

// Ручки задач требуют id мероприятия — берём его из шаблона (event_id шаблона не меняется).
const templateEventIds: Record<string, Promise<types.Event['id']>> = {};

function getTemplateEventId(
  access_token: string,
  templateId: types.Template['id']
) {
  if (!templateEventIds[templateId]) {
    templateEventIds[templateId] = getTemplate(access_token, templateId)
      .then(({ data }) => data.event_id)
      .catch((err) => {
        delete templateEventIds[templateId];
        throw err;
      });
  }
  return templateEventIds[templateId];
}

export async function createDiplomasTask(
  access_token: string,
  payload: types.DiplomasTaskPayload
) {
  const eventId = await getTemplateEventId(access_token, payload.template_id);
  return $axios.post<types.DiplomasTask>(
    `/api/diplomas/${eventId}/tasks`,
    payload,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

export async function getDiplomasTaskList(
  access_token: string,
  params: types.DiplomasTaskParams
) {
  const eventId = await getTemplateEventId(access_token, params.template_id);
  return $axios.get<types.DiplomasTask[]>(`/api/diplomas/${eventId}/tasks`, {
    headers: getAuthHeaders(access_token),
    params,
  });
}

export async function getDiplomasTask(
  access_token: string,
  id: types.DiplomasTask['id'],
  templateId: types.Template['id']
) {
  const eventId = await getTemplateEventId(access_token, templateId);
  return $axios.get<types.DiplomasTask>(
    `/api/diplomas/${eventId}/tasks/${id}`,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

export function getDiplomasProgress(
  access_token: string,
  id: types.DiplomasTask['id']
) {
  return $axios.get<types.DiplomasProgress>(
    `/api/diplomas/tasks/${id}/progress`,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

export function getDiplomasByTemplateId(
  access_token: string,
  templateId: types.Template['id'],
  params: types.DiplomasByTempateIdParams
) {
  return $axios.get<types.Diploma[]>(`/api/templates/${templateId}/diplomas`, {
    headers: getAuthHeaders(access_token),
    params,
  });
}

export function deleteDiplomasByTemplateId(
  access_token: string,
  templateId: types.Template['id']
) {
  return $axios.delete(`/api/templates/${templateId}/diplomas`, {
    headers: getAuthHeaders(access_token),
  });
}

export function deleteDiplomaById(
  access_token: string,
  diplomaId: types.Diploma['id']
) {
  return $axios.delete(`/api/diplomas/${diplomaId}`, {
    headers: getAuthHeaders(access_token),
  });
}

export function cloneTemplate(
  access_token: string,
  templateId: types.Template['id'],
  input: types.CloneTemplateInput
) {
  return $axios.post<types.Template>(
    `/api/templates/${templateId}/clone`,
    input,
    {
      headers: getAuthHeaders(access_token),
    }
  );
}

export const getBuildVersion = () => {
  return $axios.get<string>('/api/build_version');
};
